import React, { useState, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { LogOut, User, Phone } from 'lucide-react';
import styles from './ProfileDropdown.module.css';
import KSlogo from '../../../Assets/Logo-kochasoft-light.svg';

const DEFAULT_CONTACT = {
    phone: '+1 248-573-3800',
    email: 'cecsupport@kochasoft.com'
};

export const ProfileDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT);
    const dropdownRef = useRef(null);
    const { instance } = useMsal();

    const handleLogout = () => {
        const account = instance.getActiveAccount();
        instance.logoutPopup({
            account,
            postLogoutRedirectUri: "/login",
        }).catch((error) => console.error(error));
    };

    const handleCopyPhone = () => {
        navigator.clipboard.writeText(contactInfo.phone).then(() => {
            setShowToast(true);
            setTimeout(() => setShowToast(false), 2000);
        }).catch((error) => console.error('Failed to copy:', error));
    };

    useEffect(() => {
        const fetchContactInfo = async () => {
            try {
                const account = instance.getActiveAccount();
                if (!account) {
                    console.warn('No active account, using default contact info');
                    return;
                }

                const clientId = instance.getConfiguration().auth.clientId;
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: [`api://${clientId}/.default`],
                    account: account
                });

                const response = await fetch('/settings/api/effective', {
                    headers: {
                        'Authorization': `Bearer ${tokenResponse.accessToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Settings API returned ${response.status}`);
                }

                const data = await response.json();

                const supportContact = data?.settings?.GENERAL?.support_contact;
                if (supportContact) {
                    setContactInfo({
                        phone: supportContact.phone?.value || DEFAULT_CONTACT.phone,
                        email: supportContact.email?.value || DEFAULT_CONTACT.email
                    });
                }
            } catch (error) {
                console.error('Failed to fetch contact info:', error);
            }
        };

        fetchContactInfo();
    }, [instance]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.profileDropdown} ref={dropdownRef}>
            <button
                className={styles.userIcon}
                onClick={() => setIsOpen(!isOpen)}
            >
                <User size={20} />
            </button>


            {isOpen && (
                <div className={styles.dropdownMenu}>
                    <div className={styles.callCenterSection}>
                        <button
                            className={styles.phoneButton}
                            onClick={handleCopyPhone}
                            title="Kochasoft Call Center Contact number"
                        >
                            <Phone size={18} className={styles.phoneIcon} />
                            <span className={styles.phoneNumber}>{contactInfo.phone}</span>
                        </button>
                    </div>
                    <div className={styles.supportSection}>
                        <span className={styles.supportText}>
                            To report an incident, bug or feedback, please email us at{' '}
                            <a href={`mailto:${contactInfo.email}`} className={styles.emailLink}>
                                {contactInfo.email}
                            </a>
                        </span>
                    </div>
                    <div className={styles.versionContainer}>
                        <span className={styles.versionLabel}>Version</span>
                        <span className={styles.versionNumber}>1.1.0.0</span>
                    </div>

                    <div className={styles.presentedBy}>
                        <span className={styles.presentedLabel}>Presented by</span>
                        <a href="https://www.kochasoft.com" target="_blank" rel="noopener noreferrer">
                            <img src={KSlogo} alt="Company Logo" className={styles.footerLogo} />
                        </a>
                    </div>
                    <button className={styles.logoutButton} onClick={handleLogout}>
                        <LogOut size={18} />
                        <span>Log Out</span>
                    </button>
                </div>
            )}
            {showToast && (
                <div className={styles.toast}>
                    Copied
                </div>
            )}
        </div>
    )
}
